import { Proto } from "../../pojo/Proto";
import {
    GridDataResponse,
    GridDataTerm,
    GridItemChange,
    GridProfile,
    GridReservation,
    GridsService,
} from "../../services/grids.service";
import { DowGrid } from "../s25-virtual-grid/s25.dow.grid.component";
import { Grid } from "../s25-virtual-grid/s25.virtual.grid.component";
import { EventSummary, EventSummaryService } from "../s25-swarm-schedule/s25.event.summary.service";
import { S25Util } from "../../util/s25-util";
import { BOARD_CONST } from "../s25-swarm-schedule/s25.board.const";
import { BoardUtil } from "../s25-swarm-schedule/s25.board.util";
import { S25Const } from "../../util/s25-const";
import { CustomAttributes } from "../../pojo/CustomAttributes";
import { computed, signal, Signal, TemplateRef, WritableSignal } from "@angular/core";
import { DropDownItem } from "../../pojo/DropDownItem";
import { RecordType } from "../s25-event/ProfileI";
import { MPGUtil } from "../s25-meeting-pattern-grid/s25.meeting.pattern.grid.util";
import { ProfileUtil } from "../s25-datepattern/profile.util";
import { Item } from "../../pojo/Item";
import { TooltipResponse, tooltipRowType } from "../../services/tooltip.service";
import { hasEditFls } from "../../pojo/Fls";
import { AvailService, EventPerm } from "../../services/avail.service";
import { Merge } from "../../pojo/Util";

export namespace ScheduleOnlyGrid {
    import Unwrap = Proto.Unwrap;
    import DowNumber = Proto.DowNumber;
    import TimeSet = EventSummary.TimeSet;
    import ISODateString = Proto.ISODateString;
    import ISOTimeString = Proto.ISOTimeString;

    export type DataSource = DowGrid.DataSource<HeaderData, RowData, ItemData>;

    export type Data = DowGrid.Data<HeaderData, RowData, ItemData>;
    export type Row = Grid.Row<RowData>;
    export type Item = DowGrid.Item<ItemData>;

    export type HeaderData = {};
    export type RowData = TooltipData & { header: string; profileId: number };
    export type ItemData = DowGrid._CustomItemData<DowGrid.CustomItemData> &
        TooltipData & {
            profileId: number;
            profileName: string;
            profileCode: string;
            boundProfiles: number[];
            adHoc: boolean;
            instructorConflict: WritableSignal<boolean>;
            locationConflict: WritableSignal<boolean>;
            resourceConflict: WritableSignal<boolean>;
            startDate: ISODateString;
            endDate: ISODateString;
            eventStartHour: number; // startHour + setup + pre
            eventEndHour: number; // endHour - post - takedown
            setup: number; // Minutes
            pre: number; // Minutes
            post: number; // Minutes
            takedown: number; // Minutes
            dragMessage: string;
            isMakeupDate: boolean;
            isConversionDate: boolean;
            isExamDate: boolean;
            occurrences: Occurrence[];
            // Original is primarily for bulk saving, for which there are three states we need to keep track of
            // 1. The "original" item.data.original state, that is the state that the event is actually in
            // 2. The "current" item.data state, that is where the item is on the grid
            // 3. The "new" item.candidate state, that is the state we're trying to move the item to
            original: Omit<ItemData, "original">;
        };

    export type Occurrence = Merge<
        GridReservation,
        {
            locations: (GridReservation["locations"][number] & { ap: EventPerm })[];
            resources: (GridReservation["resources"][number] & { ap: EventPerm })[];
        }
    >;

    export type TooltipData = {
        eventId: number;
        eventName: string;
        eventTitle: string;
        reference: string;
        expectedHeadCount: number;
        registeredHeadCount: number;
        isLynx: boolean;
        features: { id: number; name: string }[];
        subjectCode: string;
        instructor?: EventData["roles"][number] & { name: string };
        courseReferenceNumber?: string;
        term?: GridDataTerm;
        subTerm?: string;
        sectionType?: string;
        locations: { id: number; name: string }[];
    };

    type EventData = NonNullable<Unwrap<Awaited<ReturnType<typeof GridsService.getData>>>>["events"][number];

    type ProfileData = { data: GridDataResponse; event: EventData; profile: EventData["profiles"][number] };

    export type Filter = {
        label: string;
        type: "text" | "numberRange" | "contact" | "dropdownMultiselect" | "yesNo" | "options" | "none";
        value?: WritableSignal<any>;
        active: Signal<boolean>;
        showText?: Signal<boolean>;
        clear?: () => void;
        text: Signal<string>;
        filter: (row: Item) => boolean;
        data?: unknown;
    };

    export type Conflicts = {
        types: number;
        total: number;
        instructors: ScheduleOnlyGrid.Conflict[];
        locations: ScheduleOnlyGrid.Conflict[];
        resources: ScheduleOnlyGrid.Conflict[];
    };

    export type Conflict = {
        type: "instructor" | "locationAvailability" | "resourceAvailability" | "saveMessage" | "saveError";
        item: ScheduleOnlyGrid.Item;
        items?: ScheduleOnlyGrid.Item[];
        time: string;
        message?: string;
        messages?: string[];
        load?: () => void;
    };

    export function getData(data: GridDataResponse): { rows: Row[]; items: Item[] } {
        for (const event of data.events) event.name = event.name?.trim() || "";
        data.events.sort((a, b) => a.name.localeCompare(b.name));
        const profiles = getProfileData(data);

        const rows: Row[] = getRows(profiles);
        const items: Item[] = getItems(profiles, data.changes);

        return { rows, items };
    }

    export function getProfileData(data: GridDataResponse): ProfileData[] {
        const filter = (profile: GridProfile) => {
            return profile.recType === RecordType.RecurrenceGrammar || !!profile.reservations?.length;
        };
        const profiles = data.events
            .filter((event) => !!event.profiles?.length)
            .map((event) => event.profiles.filter(filter).map((profile) => ({ data, event, profile }) as ProfileData));
        return S25Util.array.flatten(profiles);
    }

    export function getRows(profiles: ProfileData[]): Row[] {
        return profiles.map(mapProfileToRow);
    }

    export function mapProfileToRow(data: {
        data: GridDataResponse;
        event: EventData;
        profile: EventData["profiles"][number];
    }): Row {
        let header = data.event.name;
        if (data.event.profiles.length > 1) header += ` (${data.profile.name})`;
        return {
            id: data.profile.id,
            data: {
                header,
                profileId: data.profile.id,
                ...getTooltipData(data),
            },
        };
    }

    export function sortRowsAsEvents(rows: Row[]): void {
        rows.sort((a, b) => {
            if (!a.data.eventName) return 1;
            if (!b.data.eventName) return -1;
            if (!a.data.eventName && !b.data.eventName) return 0;
            return a.data.eventName.localeCompare(b.data.eventName);
        });
    }

    export function sortRowsAsInstructors(rows: Row[]): void {
        rows.sort((a, b) => {
            if (!a.data?.instructor) return 1;
            if (!b.data?.instructor) return -1;
            if (!a.data?.instructor && !b.data?.instructor) return 0;
            return a.data.instructor?.name?.localeCompare(b.data.instructor?.name);
        });
    }

    export function getItems(profiles: ProfileData[], changes: GridItemChange[]): Item[] {
        const items = S25Util.array.flatten(profiles.map(mapProfileToItems));
        linkItems(items);
        applyChanges(items, changes);

        return items;
    }

    // Applies changes to moved items
    export function applyChanges(items: Item[], changes: GridItemChange[]) {
        const map = S25Util.array.mapBy(changes, (change) => change.profileId);
        for (const item of items) {
            if (!item.data) continue;
            const change = map.get(item.data.profileId);
            if (!change) continue;
            applyChange(item, change);
        }
    }

    export function applyChange(item: Item, change: GridItemChange) {
        if (!change || !item.draggable || !item.data) return;
        const newPattern = getUpdatedPattern(item, change.pattern, change.startTime, change.endTime);
        Object.assign(item.data, newPattern);
        // TODO MULTIPLAYER: Have dow grid update its properties
    }

    export function getUpdatedPattern(
        item: Item,
        newDow: string,
        newStart: ISODateString,
        newEnd: ISODateString,
    ): Partial<Item["data"]> {
        if (!item.data) return;
        const newPattern: Partial<Item["data"]> = {};
        const startDelta = S25Util.date.dateToHours(newStart) - item.data.eventStartHour;
        let endDelta = newEnd ? S25Util.date.dateToHours(newEnd) - item.data.eventEndHour : startDelta;
        newPattern.startHour = item.data.startHour + startDelta;
        newPattern.endHour = item.data.endHour + endDelta;
        if (newPattern.endHour <= newPattern.startHour) {
            newPattern.endHour += 24;
            endDelta = newPattern.endHour - item.data.endHour;
        }
        newPattern.eventStartHour = item.data.eventStartHour + startDelta;
        newPattern.eventEndHour = item.data.eventEndHour + endDelta;

        if (item.data.dow === newDow) {
            newPattern.occurrences = item.data.occurrences.map((occ) => ({
                ...occ,
                start: S25Util.date.addHoursToS25ISODateTimeStr(occ.start, startDelta),
                end: S25Util.date.addHoursToS25ISODateTimeStr(occ.end, endDelta),
            }));
            newPattern.profileCode = item.data.profileCode;
        } else {
            const newDows = new Set(newDow.split(""));
            const profileModel = ProfileUtil.getProfileModel(null, item.data.profileCode, null);
            for (const dow of profileModel.daysOfWeek!) {
                dow.chosen = newDows.has(BOARD_CONST.dowMap[dow.abbr as keyof typeof BOARD_CONST.dowMap]);
            }

            // Set start and end date to the correct date for the first occurrence
            const firstDowIndex = profileModel.daysOfWeek!.findIndex((dow) => dow.chosen);
            let startDt = S25Util.date.changeDow(
                S25Util.date.parseDropTZ(item.data.occurrences[0].start),
                firstDowIndex,
            );

            // If start date is before term start, place start date on the first dow after start
            const termStart = S25Util.date.parse(item.data.term?.start_dt);
            if (item.data.term && termStart > startDt) {
                const termStartDow = termStart.getDay();
                let newDowIndex = profileModel.daysOfWeek.findIndex(
                    (dow) => dow.chosen && Number(dow.itemId) >= termStartDow,
                );
                // If new dow is later in the week just change the dow
                // If it is earlier in the week change the dow and add a week
                if (newDowIndex !== -1) {
                    startDt = S25Util.date.changeDow(termStart, newDowIndex);
                } else {
                    startDt = S25Util.date.changeDow(termStart, firstDowIndex);
                    startDt = S25Util.date.addWeeks(startDt, 1);
                }
            }

            S25Util.date.syncDateToTime(startDt, S25Util.date.parseDropTZ(newStart));
            const duration = S25Util.date.diffMinutes(
                S25Util.date.parseDropTZ(newStart),
                S25Util.date.parseDropTZ(newEnd),
            );
            const endDt = S25Util.date.addMinutes(S25Util.date.clone(startDt), duration);

            // Get new occurrences
            const timeModel = {
                evStartDt: startDt,
                evEndDt: endDt,
                minutes: {
                    setupMinutes: item.data.setup,
                    preMinutes: item.data.pre,
                    postMinutes: item.data.post,
                    takedownMinutes: item.data.takedown,
                },
            };
            let newOccs = ProfileUtil.getOccurrences(timeModel, profileModel);

            // Remove any occurrences after term end date
            if (item.data.term?.end_dt) {
                const termEnd = S25Util.date.toEndOfDay(item.data.term.end_dt);
                newOccs = newOccs.filter((occ) => occ.evEndDt <= termEnd);
            }

            // Assume that location and resources are consistent across occurrences
            // In the future we can consider occurrence mapping like we do elsewhere
            const locations = item.data.occurrences[0].locations;
            const resources = item.data.occurrences[0].resources;

            newPattern.occurrences = newOccs.map((occ, i) => ({
                id: item.data.occurrences[i]?.id,
                start: S25Util.date.toS25ISODateTimeStrFromDate(occ.evStartDt),
                end: S25Util.date.toS25ISODateTimeStrFromDate(occ.evEndDt),
                commentId: null as Occurrence["commentId"],
                locations: S25Util.deepCopy(locations),
                resources: S25Util.deepCopy(resources),
            }));
            newPattern.profileCode = ProfileUtil.getProfileCodeByModel(profileModel);
            newPattern.dow = newDow;
        }
        newPattern.occurrences.sort(occurrenceSort);

        const [earliestReservation, lastReservation] = getReservationRange(newPattern.occurrences);
        newPattern.startDate = earliestReservation;
        newPattern.endDate = lastReservation;

        return newPattern;
    }

    export function occurrenceSort(a: ScheduleOnlyGrid.Occurrence, b: ScheduleOnlyGrid.Occurrence) {
        if (a.start === b.start) return 0;
        return a.start < b.start ? -1 : 1;
    }

    export function mapProfileToItems(
        data: ProfileData,
        index: number,
        profiles: { event: EventData; profile: EventData["profiles"][number] }[],
    ): Item[] {
        const draggable = isDraggable(data);

        if (data.profile.recType === RecordType.RecurrenceGrammar) {
            const item = {
                id: `${data.profile.id}`,
                top: (index / profiles.length) * 100,
                height: 100 / profiles.length,
                draggable: draggable.draggable,
                linkedItems: new Set(),
                data: getItemData(data, draggable, null),
            } as Item;
            item.minimapColor = getMinimapColor(item);
            return [item];
        } else {
            const occs = mapOccurrenceDates(data.profile.reservations);
            const orderedDowPatterns = BoardUtil.getDowPatterns(occs);
            const timeSets = EventSummaryService._adhocSummaryModel(occs, -1, 99_999, orderedDowPatterns, true, true);

            return timeSets.map((set, i) => {
                const item = {
                    id: `${data.profile.id}-${i}`,
                    top: (index / profiles.length) * 100,
                    height: 100 / profiles.length,
                    draggable: draggable.draggable,
                    linkedItems: new Set(),
                    data: getItemData(data, draggable, set),
                } as Item;
                item.minimapColor = getMinimapColor(item);
                return item;
            });
        }
    }

    export function isDraggable(data: ProfileData): { draggable: boolean; message?: string } {
        if (!hasEditFls(data.event.ols)) {
            return { draggable: false, message: "You lack permission to edit this section" };
        }

        if (data.profile.recType !== RecordType.RecurrenceGrammar) {
            return { draggable: false, message: `Ad hoc sections cannot be moved` };
        }

        const profileModel = ProfileUtil.getProfileModel(null, data.profile.profileCode, null);
        if (profileModel.type !== "weekly") {
            const type = S25Util.firstCharToUpper(profileModel.type);
            return { draggable: false, message: `${type} repeating sections cannot be moved` };
        }

        // Check unassign AP
        const locations = data.profile.reservations.flatMap((res) => res.locations.map((loc) => loc.id));
        const canUnassignLocations = locations.every((loc) => {
            const perm = data.data.spaceAp[loc];
            if (perm?.assignOverride) return true;
            return AvailService.isPermEnoughTo("unassign", { assign: perm?.assignPerm, unassign: perm?.unassignPerm });
        });
        if (!canUnassignLocations) {
            return { draggable: false, message: `You lack permission to move one or more location on this section` };
        }

        const resources = data.profile.reservations.flatMap((res) => res.resources.map((res) => res.id));
        const canUnassignResources = resources.every((res) => {
            const perm = data.data.resourceAp[res];
            if (perm?.assignOverride) return true;
            return AvailService.isPermEnoughTo("unassign", { assign: perm?.assignPerm, unassign: perm?.unassignPerm });
        });
        if (!canUnassignResources) {
            return { draggable: false, message: `You lack permission to move one or more resource on this section` };
        }

        // If locations and resources are not consistent across occurrences we can't easily change the meeting pattern
        // So make those immovable, at least to start with.
        if (!areLocationsAndResourcesConsistent(data.profile.reservations)) {
            const message = `Occurrences in this section do not have consistent location and/or resource reservations`;
            return { draggable: false, message };
        }

        return { draggable: true };
    }

    export function areLocationsAndResourcesConsistent(occurrences: GridReservation[]): boolean {
        const allLocations = new Set(occurrences.flatMap((occ) => occ.locations.map((loc) => loc.id)));
        const locationsConsistent = occurrences.every((occ) => {
            if (occ.locations?.length !== allLocations.size) return false;
            return occ.locations.every((loc) => allLocations.has(loc.id));
        });
        const allResources = new Set(occurrences.flatMap((occ) => occ.resources.map((res) => res.id)));
        const resourcesConsistent = occurrences.every((occ) => {
            if (occ.resources?.length !== allResources.size) return false;
            return occ.resources.every((res) => allResources.has(res.id));
        });

        return locationsConsistent && resourcesConsistent;
    }

    // TimeSet is only needed for non-recurring profiles
    export function getItemData(
        data: ProfileData,
        drag: { draggable: boolean; message?: string },
        timeSet: TimeSet,
    ): ItemData {
        const { profile, event } = data;
        const [earliestReservation, lastReservation] = getReservationRange(profile.reservations);

        let dow: string, eventStartHour: number, eventEndHour: number;
        if (!timeSet) {
            const model = ProfileUtil.getProfileModel(null, data.profile.profileCode, null);
            dow = ProfileUtil.getShortDowPattern(model);
            eventStartHour = S25Util.date.dateToHours(data.profile.start);
            eventEndHour = S25Util.date.dateToHours(data.profile.end);
        } else {
            dow = getDowFromTimeSet(timeSet);
            eventStartHour = S25Util.date.timeToHours(timeSet.startTime);
            eventEndHour = S25Util.date.timeToHours(timeSet.endTime);
        }

        const startHour = eventStartHour - (data.event.setup + data.profile.pre) / 60;
        const endHour = eventEndHour + (data.event.takedown + data.profile.post) / 60;

        const occurrences = profile.reservations.map((res) => {
            return {
                ...res,
                locations: res.locations.map((loc) => ({ ...loc, ap: data.data.spaceAp[loc.id] })),
                resources: res.resources.map((res) => ({ ...res, ap: data.data.resourceAp[res.id] })),
            };
        });

        const itemData = {
            profileId: profile.id,
            profileName: profile.name,
            profileCode: profile.profileCode,
            boundProfiles: profile.boundProfiles,
            adHoc: profile.recType !== RecordType.RecurrenceGrammar,
            dow,
            startHour,
            endHour,
            preHours: (data.event.setup + data.profile.pre) / 60,
            postHours: (data.event.takedown + data.profile.post) / 60,
            eventStartHour,
            eventEndHour,
            snapOffsetX: -(data.event.setup + data.profile.pre) / 60,
            unResizableHours: (data.event.setup + data.profile.pre + data.profile.post + data.event.takedown) / 60,
            setup: data.event.setup,
            pre: data.profile.pre,
            post: data.profile.post,
            takedown: data.event.takedown,
            startDate: earliestReservation,
            endDate: lastReservation,
            occurrences,
            original: {} as ScheduleOnlyGrid.ItemData, // Set below
            dragMessage: drag.draggable ? null : drag.message,
            instructorConflict: signal(false),
            locationConflict: signal(false),
            resourceConflict: signal(false),
            isMakeupDate: data.profile.alienUID?.startsWith("LYNX-MOD") && data.profile.name.startsWith("MAKEUP"),
            isConversionDate:
                data.profile.alienUID?.startsWith("LYNX-MOD") && data.profile.name.startsWith("CONVERSION"),
            isExamDate: data.profile.alienUID?.startsWith("LYNX-FX-RSRV") && data.profile.name.startsWith("EXAM"),
            ...getTooltipData(data),
        };
        Object.assign(itemData.original, itemData);

        return itemData;
    }

    export function getReservationRange(reservations: GridReservation[]): [ISODateString, ISODateString] {
        const earliestReservation = reservations.reduce(
            (earliest, res) => (res.start < earliest ? res.start : earliest),
            reservations[0].start,
        );
        const lastReservation = reservations.reduce(
            (latest, res) => (res.end > latest ? res.end : latest),
            reservations[0].end,
        );
        return [earliestReservation, lastReservation];
    }

    // Link bound items with the same patterns and times
    export function linkItems(items: Item[]) {
        const itemsByProfile = S25Util.array.groupBy(items, (item) => item.data.profileId);

        for (const item of items) {
            if (!item.data.boundProfiles?.length) continue;
            for (const profile of item.data.boundProfiles) {
                const boundItems = itemsByProfile[profile];
                for (const binding of boundItems ?? []) {
                    if (item.data.dow !== binding.data.dow) continue; // Ignore bindings on different patterns

                    // Immobilize back to back bindings
                    // Immobilize overlapping but non-identical bindings
                    const overlapping = isOverlapping(item, binding);
                    const sameTime =
                        item.data.startHour === binding.data.startHour && item.data.endHour === binding.data.endHour;
                    let dragMessage;
                    if (!overlapping) {
                        dragMessage = `Section is subject to a back to back binding. To move this section break the binding through LYNX`;
                    } else if (!sameTime) {
                        dragMessage = `Section is overlapping with another bound section. To move this section break the binding through LYNX`;
                    }
                    if (!!dragMessage) {
                        item.draggable = false;
                        binding.draggable = false;
                        item.data.dragMessage = dragMessage;
                        binding.data.dragMessage = dragMessage;
                    }

                    item.linkedItems.add(binding.id);
                    binding.linkedItems.add(item.id);
                }
            }
        }
    }

    function isOverlapping(a: Item, b: Item) {
        return a.data.dow === b.data.dow && a.data.startHour <= b.data.endHour && a.data.endHour >= b.data.startHour;
    }

    export function getTooltipData(data: ProfileData): TooltipData {
        const { event, profile } = data;
        const roleById = new Map(S25Util.array.forceArray(event.roles).map((role) => [role.role, role]));
        const attributeById = new Map(S25Util.array.forceArray(event.attributes).map((ca) => [ca.id, ca]));
        const primaryOrg = event.primaryOrganization;

        const locations: TooltipData["locations"] = [];
        const locationIds = new Set<number>();
        for (const res of profile.reservations) {
            for (const location of res.locations || []) {
                if (locationIds.has(location.id)) continue;
                locationIds.add(location.id);
                locations.push(location);
            }
        }

        const isLynx = event.alienUid?.toLowerCase().startsWith("lynx");
        const termName = attributeById.get(CustomAttributes.system.termCode.id)?.value;
        const instructor = roleById.get(S25Const.instructorRole.event) as TooltipData["instructor"];
        if (instructor) instructor.name = getContactName(instructor);

        return {
            eventId: event.id,
            eventName: event.name,
            eventTitle: event.title,
            reference: event.locator,
            isLynx,
            expectedHeadCount: Number(profile.expectedHeadCount),
            registeredHeadCount: Number(profile.registeredHeadCount),
            features: data.profile.featurePreferences,
            subjectCode: isLynx ? primaryOrg?.name : null,
            instructor,
            courseReferenceNumber: attributeById.get(CustomAttributes.system.uniqueSectionId.id)?.value,
            subTerm: attributeById.get(CustomAttributes.system.subTerm.id)?.value,
            sectionType: attributeById.get(CustomAttributes.system.sectionType.id)?.value,
            locations,
            term: data.data.terms[termName],
        };
    }

    export function getContactName(contact: { firstName: string; familyName: string }) {
        if (!contact.firstName && !contact.familyName) return "";
        if (!contact.firstName) return contact.familyName.trim();
        if (!contact.familyName) return contact.firstName.trim();
        return `${contact.familyName?.trim()}, ${contact.firstName?.trim()}`;
    }

    export function getDowFromTimeSet(timeSet: EventSummary.TimeSet): string {
        if (timeSet.pattern) {
            const profileCode = EventSummaryService.adhocSummaryModelToProfileCode(timeSet);
            const dow = S25Const.dows.filter((dow) => profileCode.includes(dow));
            return BoardUtil.daysOfWeekMap(dow.join(" ") + " ");
        } else {
            let dows = timeSet.map((occ) => S25Util.date.parseDropTZ(occ.reservation_start_dt).getDay() as DowNumber);
            dows = S25Util.array.unique(dows).sort();
            const dowsAbbr = dows.map((dow) => BOARD_CONST.dowInt2Abbr[dow]);
            return BoardUtil.daysOfWeekMap(dowsAbbr.join(" ") + " ");
        }
    }

    export function createFilter(options: {
        type: Filter["type"];
        label: string;
        transform?: (item: Item) => any;
        data?: unknown;
    }): Filter {
        const { type, label, transform, data } = options;
        switch (type) {
            case "text":
                const textValue = signal<string | undefined>(undefined);
                return {
                    label,
                    type,
                    value: textValue,
                    active: computed(() => !!textValue()),
                    text: computed(() => textValue() as string),
                    filter: (row) => !!transform?.(row)?.toLowerCase().includes(textValue()?.toLowerCase()),
                };
            case "numberRange":
                const numberRange = signal<{ min: WritableSignal<number | null>; max: WritableSignal<number | null> }>({
                    min: signal(null),
                    max: signal(null),
                });
                return {
                    label,
                    type,
                    value: numberRange,
                    active: computed(() => numberRange().min() !== null || numberRange().max() !== null),
                    clear: () => numberRange.set({ min: signal(null), max: signal(null) }),
                    text: computed(() => {
                        const [min, max] = [numberRange().min(), numberRange().max()];
                        if (min === null) return `< ${max}`;
                        if (max === null || max < min) return `> ${min}`;
                        return `${min} - ${max}`;
                    }),
                    filter: (row) => {
                        const [min, max] = [numberRange().min(), numberRange().max()];
                        const value = transform?.(row);
                        if (isNaN(value)) return false;
                        if (min === null) {
                            if (max === null) return true;
                            return value <= max;
                        } else {
                            if (max === null || max < min) return value >= min;
                            return value >= min && value <= max;
                        }
                    },
                };
            case "contact":
                const contactValue = signal<DropDownItem | undefined>(undefined);
                return {
                    label,
                    type,
                    value: contactValue,
                    active: computed(() => !!contactValue()),
                    text: computed(() => contactValue()?.itemName ?? ""),
                    filter: (row) => {
                        return transform?.(row) === contactValue()?.itemId;
                    },
                };
            case "dropdownMultiselect":
                const multiselectValue = signal<DropDownItem[] | undefined>(undefined);
                const multiselectIds = computed(() => new Set(multiselectValue().map((item) => String(item.itemId))));
                return {
                    label,
                    type,
                    value: multiselectValue,
                    active: computed(() => !!multiselectValue()?.length),
                    text: computed(
                        () =>
                            multiselectValue()
                                ?.map((item) => item.itemName)
                                .join(", ") ?? "",
                    ),
                    filter: (row) => {
                        return transform?.(row).some((id: number | string) => multiselectIds().has(String(id)));
                    },
                    data,
                };
            // Only includes item if a selected option is true
            case "options": {
                const value = signal<DropDownItem[]>([]);
                return {
                    label,
                    type,
                    value: value,
                    active: computed(() => true),
                    showText: computed(() => !!value().length),
                    text: computed(() =>
                        value()
                            .map((i) => i.itemName)
                            .join(", "),
                    ),
                    clear: () => value.set([]),
                    filter: (item) => {
                        const data = transform?.(item);
                        if (data.skip) return true;
                        return value().some((val) => !!val.itemId && data[val.itemId] === true);
                    },
                    data,
                };
            }
            case "yesNo":
                const yesNoValue = signal<boolean | undefined>(undefined);
                return {
                    label,
                    type,
                    value: yesNoValue,
                    active: computed(() => yesNoValue() !== undefined),
                    text: computed(() => (yesNoValue() ? "Yes" : "No")),
                    filter: (row) => transform?.(row) === yesNoValue(),
                };
        }
    }

    export function mapOccurrenceDates(occs: GridReservation[]) {
        return occs.map((occ) => ({ reservation_start_dt: occ.start, reservation_end_dt: occ.end }));
    }

    export function doOccurrencesOverlap(a: GridReservation[], b: GridReservation[]) {
        return MPGUtil.doOccurrencesOverlap(mapOccurrenceDates(a), mapOccurrenceDates(b));
    }

    export async function eventTooltipService(
        overlappingItemsTemplate: TemplateRef<any>,
        itemType: Item.Id,
        eventId: number,
        reservationId: number,
        item: ScheduleOnlyGrid.Item,
    ): Promise<TooltipResponse> {
        const eventData = item.data;
        const rows: TooltipResponse["item"]["rows"]["data"] = [];

        if (eventData.eventTitle) {
            rows.push({
                name: "Title:",
                type: tooltipRowType.Raw,
                value: eventData.eventTitle,
            });
        }

        rows.push({
            name: "Head Count:",
            type: tooltipRowType.LabelledList,
            value: [
                { label: "Expected", value: eventData.expectedHeadCount || 0 },
                { label: "Registered", value: eventData.registeredHeadCount || 0 },
            ],
        });

        if (eventData.features?.length) {
            rows.push({
                name: "Features:",
                type: tooltipRowType.Bullets,
                value: eventData.features.map((feature) => ({ value: feature.name })),
            });
        }

        if (eventData.instructor) {
            const contact = eventData.instructor;
            rows.push({
                name: "Instructor:",
                type: tooltipRowType.Item,
                value: { itemTypeId: Item.Ids.Contact, itemId: contact.id, itemName: contact.name },
            });
        }

        if (eventData.locations?.length) {
            rows.push({
                name: "Locations:",
                type: tooltipRowType.Bullets,
                value: eventData.locations.map((location) => {
                    return {
                        nested: true,
                        value: {
                            type: tooltipRowType.Item,
                            value: { itemTypeId: Item.Ids.Location, itemId: location.id, itemName: location.name },
                        },
                    };
                }),
            });
        }

        if (eventData.sectionType) {
            rows.push({
                name: "Section Type:",
                type: tooltipRowType.Raw,
                value: eventData.sectionType,
            });
        }

        if (eventData.subjectCode) {
            rows.push({
                name: "Subject Code:",
                type: tooltipRowType.Raw,
                value: eventData.subjectCode,
            });
        }

        if (eventData.subTerm) {
            rows.push({
                name: "Subterm:",
                type: tooltipRowType.Raw,
                value: eventData.subTerm,
            });
        }

        if (eventData.courseReferenceNumber) {
            rows.push({
                name: "Course Reference Number:",
                type: tooltipRowType.Raw,
                value: eventData.courseReferenceNumber,
            });
        }

        if (eventData.reference) {
            rows.push({
                name: "Reference:",
                type: tooltipRowType.Raw,
                value: eventData.reference,
            });
        }

        if (item.overlapping?.length) {
            rows.push({
                name: "Overlapping Items:",
                type: tooltipRowType.Template,
                value: {
                    template: overlappingItemsTemplate,
                    context: { item },
                },
            });
        }

        if (eventData.dragMessage) {
            rows.push({
                name: "Section Cannot Be Moved:",
                type: tooltipRowType.Raw,
                value: eventData.dragMessage,
            });
        }

        return {
            item: {
                event_id: eventData.eventId,
                event_name: eventData.eventName,
                rows: { data: rows },
            },
        };
    }

    export function updateCandidateData(items: Item[]) {
        for (const item of items) {
            const startHour = item.candidate.startHour + (item.candidate.setup + item.candidate.pre) / 60;
            const endHour = item.candidate.endHour - (item.candidate.post + item.candidate.takedown) / 60;
            const startHourStr = S25Util.date.toS25ISODateTimeStrFromHours(startHour);
            const endHourStr = S25Util.date.toS25ISODateTimeStrFromHours(endHour);
            const newPattern = getUpdatedPattern(item, item.candidate.dow, startHourStr, endHourStr);
            item.candidate.profileName = getNewProfileNameFromItem(item);
            Object.assign(item.candidate, newPattern);
        }
    }

    export function hasItemChanged(item: Item): boolean {
        if (item.candidate.dow !== item.data.original.dow) return true;
        if (item.candidate.startHour !== item.data.original.startHour) return true;
        if (item.candidate.endHour !== item.data.original.endHour) return true;
        return false;
    }

    export function mapByProfile(items: Item[]): Map<number, Item> {
        return S25Util.array.mapBy(items, (item) => item.data.profileId);
    }

    export function getTimeString(item: Item, is24Hours: boolean): string {
        const startTime = S25Util.date.toTimeStrFromHours(item.candidate.startHour, is24Hours);
        const endTime = S25Util.date.toTimeStrFromHours(item.candidate.endHour, is24Hours);
        return `${startTime} - ${endTime}`;
    }

    export function updateOriginalsWithCandidates(items: Item[]) {
        for (const item of items) Object.assign(item.data.original, item.candidate);
    }

    export function getOccurrenceDatesWithAdditionalTime(
        item: Item,
        occ: GridReservation,
    ): {
        start: ISODateString;
        end: ISODateString;
    } {
        const pre = (item.candidate.setup + item.candidate.pre) / 60;
        const post = (item.candidate.post + item.candidate.takedown) / 60;
        const start = S25Util.date.addHoursToS25ISODateTimeStr(occ.start, -pre);
        const end = S25Util.date.addHoursToS25ISODateTimeStr(occ.end, post);
        return { start, end };
    }

    export function getMinimapColor(item: Item) {
        // Needs to be kept up to date with legend
        return computed(() => {
            if (!item.data._fitsSchedule()) return "#ff5959";
            if (item.data.instructorConflict()) return "#ba92e1";
            if (item.data.locationConflict()) return "#0077d7";
            if (item.data.resourceConflict()) return "#e91e63";
            if (item.data.adHoc) return "#d2d2d2";
            if (!!item.data.locations.length) return "#bbdfff";
            return "#cff3d2";
        });
    }

    export function getNewProfileNameFromItem(item: Item): string {
        const { profileName, dow, occurrences } = item.candidate;
        const date = S25Util.date.toS25ISODateStr(occurrences[0].start);
        const start = S25Util.date.toS25ISOTimeStr(occurrences[0].start);
        const end = S25Util.date.toS25ISOTimeStr(occurrences[0].end);
        return getNewProfileName(profileName, dow, date, start, end);
    }

    export function getNewProfileName(
        current: string,
        dow: string,
        startDate: ISODateString,
        startTime: ISOTimeString,
        endTime: ISOTimeString,
    ): string {
        if (!current) return current;
        const re = /(?<pattern>\w+)\s+(?<start>\d\d\d\d)-(?<end>\d\d\d\d)(?:\s+(?<type>\S+))?\s+(?<date>\d\d\/\d\d)/;
        const match = current.match(re);
        if (!match) return current;
        const start = startTime.slice(0, 5).replace(":", "");
        const end = endTime.slice(0, 5).replace(":", "");
        const type = match.groups.type;
        const date = startDate.slice(5).replace("-", "/");

        if (type) return current.replace(re, `${dow} ${start}-${end} ${type} ${date}`);
        return current.replace(re, `${dow} ${start}-${end} ${date}`);
    }
}
